import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import API_ENDPOINTS from '../../../config/api';
import { useTranslation } from '../../../hooks/useTranslation';
import useInstanceRecord from '../../../hooks/useInstanceRecord';
import useContentRefresh from '../../../hooks/useContentRefresh';
import ClusterCard from './ClusterCard';
import PainPointRow from './PainPointRow';
import ScenarioProposal from './ScenarioProposal';

/**
 * The facilitator's pre-day screen (specs/prework-curation.md): every pain
 * point the participants wrote, grouped by what they mean, ranked within a
 * scenario, and promoted to muuttujat one tick at a time. The AI proposes
 * the groups, the titles and a scenario; nothing becomes a muuttuja until
 * the facilitator says so.
 *
 * The runs — grouping, the scenario proposal, finishing the vote — are the
 * module's admin actions (kipupisteet.v1 → kipupisteet_v1.*), pressed in the
 * section's action shelf; the vote's switch is its live control. This panel
 * holds the decisions: it refetches on the jobs' content events and shows the
 * latest proposal job's result for editing (specs/facilitator-console.md).
 */

const MUUTTUJA_CAP = 6;

const Wrap = styled.section`
  padding: 32px 40px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;
  gap: 16px;
  @media (max-width: 640px) { padding: 24px 16px; }
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  h2 { margin: 0; font-size: 20px; font-weight: 700; }
  p { margin: 0; color: rgba(255, 255, 255, 0.55); font-size: 13px; }
`;

const Counts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  b { color: #fff; font-weight: 600; }
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  h3 { margin: 6px 0 0; font-size: 14px; font-weight: 600; color: rgba(255, 255, 255, 0.75); text-transform: uppercase; letter-spacing: 0.06em; }
`;

const ErrorLine = styled.div`
  color: #ff8a85;
  font-size: 13px;
`;

const Muted = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
`;

async function call(url, { method = 'GET', body } = {}) {
  const res = await fetch(url, {
    method, credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

export default function PreworkCurationAdmin({ instanceId, scenarios: initialScenarios = [], locked = false, step = null }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const scenarios = data?.scenarios || initialScenarios;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // The latest scenario-proposal job, read with the step's actions; none without a step.
  const [proposalJob, setProposalJob] = useState(null);

  const load = useCallback(() => Promise.all([
    call(API_ENDPOINTS.PREWORK_CLUSTERS(instanceId)).then(setData),
    step?.id
      ? call(API_ENDPOINTS.CHAIN_STEP_ADMIN_ACTIONS(instanceId, step.id))
        .then((d) => setProposalJob((d.actions || []).find((a) => a.id === 'propose_scenarios')?.latestJob || null))
        .catch(() => setProposalJob(null))
      : Promise.resolve(),
  ]).catch((e) => setError(e.message)), [instanceId, step?.id]);
  useEffect(() => { load(); }, [load]);
  // The jobs say when they have grouped, proposed or promoted (emitContentChanged 'prework')
  useContentRefresh(instanceId, ['prework'], load, { minIntervalMs: 2000 });

  const run = async (fn) => {
    setBusy(true); setError(null);
    try { const next = await fn(); if (next?.clusters) setData(next); else await load(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  // The room vote's state lives in instance settings (kipupisteet.v1's live control
  // at the top of the section switches it); the /kipupisteet screen reads the same flag.
  const { instance } = useInstanceRecord(instanceId);
  const stored = instance?.settings?.preworkVoting || {};
  const voting = { open: stored.open === true, budget: Number(stored.budget) || 3 };
  const patch = (id, body) => run(() => call(API_ENDPOINTS.PREWORK_CLUSTER(id), { method: 'PATCH', body }));
  const promote = (id) => run(() => call(API_ENDPOINTS.PREWORK_CLUSTER_ACTION(id, 'promote'), { method: 'POST' }));
  const demote = (id) => run(() => call(API_ENDPOINTS.PREWORK_CLUSTER_ACTION(id, 'demote'), { method: 'POST' }));
  const remove = (id) => run(() => call(API_ENDPOINTS.PREWORK_CLUSTER(id), { method: 'DELETE' }));
  // Move a pain point: into another cluster, or ('' target) into a new cluster of its own.
  const move = (from, nodeId, target) => run(() => {
    if (target === '') return call(API_ENDPOINTS.PREWORK_CLUSTERS(instanceId), { method: 'POST', body: { memberNodeIds: [nodeId] } });
    const to = data.clusters.find((c) => c.id === target);
    return call(API_ENDPOINTS.PREWORK_CLUSTER(target), { method: 'PATCH', body: { memberNodeIds: [...to.members.map((m) => m.nodeId), nodeId] } });
  });
  const addUnclustered = (nodeId, target) => move(null, nodeId, target);
  // Scenarios from the pre-work: the proposal job's result, edited, then what was confirmed.
  const createScenarios = (proposal) => run(() => call(API_ENDPOINTS.PREWORK_SCENARIOS(instanceId), { method: 'POST', body: { scenarios: proposal } }));

  const byScenario = useMemo(() => {
    if (!data) return [];
    const groups = new Map();
    for (const c of data.clusters) {
      const key = c.scenarioId || '';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(c);
    }
    const order = [...scenarios.map((s) => s.id), ''];
    return order.filter((k) => groups.has(k)).map((k) => ({
      scenarioId: k,
      title: k ? scenarios.find((s) => s.id === k)?.title || t('preworkCuration.scenarioFallback') : t('preworkCuration.noScenario'),
      clusters: groups.get(k),
      promoted: groups.get(k).filter((c) => c.status === 'promoted').length,
    }));
  }, [data, scenarios, t]);

  if (!data && !error) return <Wrap><Muted>{t('preworkCuration.loading')}</Muted></Wrap>;

  const painPoints = data?.painPoints || [];
  const participants = new Set(painPoints.map((p) => p.participantId)).size;
  const promotedCount = (data?.clusters || []).filter((c) => c.status === 'promoted').length;
  const proposedTargets = (data?.clusters || []).filter((c) => c.status === 'proposed');

  return (
    <Wrap>
      <Head>
        <div>
          <h2>{t('preworkCuration.title')}</h2>
          <p>{t('preworkCuration.intro')}</p>
        </div>
      </Head>
      <Counts>
        <span><b>{painPoints.length}</b> {t('preworkCuration.countPainPoints')}</span>
        <span><b>{participants}</b> {t('preworkCuration.countParticipants')}</span>
        <span><b>{data?.clusters?.length || 0}</b> {t('preworkCuration.countGroups')}</span>
        <span><b>{promotedCount}</b> {t('preworkCuration.countPromoted')}</span>
        {painPoints.some((p) => !p.embedded) && <span>{t('preworkCuration.unembedded', { count: painPoints.filter((p) => !p.embedded).length })}</span>}
      </Counts>
      {error && <ErrorLine>{error}</ErrorLine>}
      {!painPoints.length && <Muted>{t('preworkCuration.noPainPoints')}</Muted>}
      {painPoints.length > 0 && !data?.clusters?.length && <Muted>{t('preworkCuration.noClusters')}</Muted>}
      {data?.clusters?.length > 0 && (
        <Counts>
          <span>{voting.open ? t('preworkCuration.voteOpenInfo', { budget: voting.budget }) : t('preworkCuration.voteClosedInfo')}</span>
        </Counts>
      )}
      {data?.clusters?.some((c) => c.status === 'proposed') && (
        <ScenarioProposal clusters={data.clusters} busy={busy} locked={locked}
                          proposalJob={proposalJob} onCreate={createScenarios} />
      )}

      {byScenario.map((g) => (
        <Group key={g.scenarioId}>
          <h3>{t('preworkCuration.groupHeading', { title: g.title, count: g.promoted })}</h3>
          {g.clusters.map((c, i) => (
            <ClusterCard key={c.id} cluster={c} index={i} scenarios={scenarios} busy={busy} locked={locked}
                         others={proposedTargets.filter((o) => o.id !== c.id)}
                         overCap={g.promoted >= MUUTTUJA_CAP}
                         onPatch={patch} onMove={move} onPromote={promote} onDemote={demote} onDelete={remove} />
          ))}
        </Group>
      ))}

      {data?.unclustered?.length > 0 && (
        <Group>
          <h3>{t('preworkCuration.unclusteredHeading', { count: data.unclustered.length })}</h3>
          {data.unclustered.map((p) => (
            <PainPointRow key={p.nodeId} painPoint={p} targets={proposedTargets} locked={busy || locked}
                          onMove={(target) => addUnclustered(p.nodeId, target)} />
          ))}
        </Group>
      )}
    </Wrap>
  );
}
